import React, { useRef, useState } from 'react';
import { Video, Image as ImageIcon, Upload, Trash2, Edit2, Save, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const MultimediaSection = ({ youtubeUrl, galleryImages, isAdmin, onImageUpload, onImageDelete, onYoutubeUpdate }) => {
  const imageInputRef = useRef(null);
  const [isEditingYoutube, setIsEditingYoutube] = useState(false);
  const [tempYoutubeUrl, setTempYoutubeUrl] = useState(youtubeUrl || '');

  const handleImageSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onImageUpload(files);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-6 animate-slideIn">
      {/* Video Section */}
      <div className="glass rounded-2xl p-6 shadow-xl border border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Video className="w-5 h-5 text-blue-400" />
            Vidéo de Présentation
          </h2>
          {isAdmin && (
            <div className="flex gap-2">
              {isEditingYoutube ? (
                <>
                  <Button
                    onClick={() => {
                      onYoutubeUpdate(tempYoutubeUrl);
                      setIsEditingYoutube(false);
                    }}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="save-youtube-btn"
                  >
                    <Save className="w-4 h-4 mr-1" />
                    Sauvegarder
                  </Button>
                  <Button
                    onClick={() => {
                      setTempYoutubeUrl(youtubeUrl || '');
                      setIsEditingYoutube(false);
                    }}
                    size="sm"
                    variant="outline"
                    data-testid="cancel-youtube-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Annuler
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => setIsEditingYoutube(true)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="edit-youtube-btn"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
              )}
            </div>
          )}
        </div>
        
        {isEditingYoutube ? (
          <div className="space-y-2">
            <Input
              value={tempYoutubeUrl}
              onChange={(e) => setTempYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/embed/VIDEO_ID"
              className="bg-slate-800 border-slate-600 text-white"
              data-testid="youtube-url-input"
            />
            <p className="text-xs text-gray-400">
              Format: https://www.youtube.com/embed/VIDEO_ID
            </p>
          </div>
        ) : youtubeUrl ? (
          <div className="aspect-video rounded-lg overflow-hidden">
            <iframe
              src={youtubeUrl}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="youtube-video"
            />
          </div>
        ) : (
          <div className="aspect-video rounded-lg bg-slate-700/30 flex items-center justify-center">
            <p className="text-gray-400" data-testid="no-video-msg">
              Aucune vidéo configurée
            </p>
          </div>
        )}
      </div>

      {/* Gallery Section */}
      <div className="glass rounded-2xl p-6 shadow-xl border border-slate-700/50">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-blue-400" />
            Galerie
          </h2>
          {isAdmin && (
            <Button
              onClick={() => imageInputRef.current?.click()}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="upload-image-btn"
            >
              <Upload className="w-4 h-4 mr-1" />
              Ajouter
            </Button>
          )}
        </div>

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageSelect}
          className="hidden"
          data-testid="image-input"
        />

        {/* Debug info */}


        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {galleryImages.length === 0 ? (
            <p className="col-span-full text-gray-400 text-center py-8" data-testid="no-images-msg">
              Aucune image dans la galerie
            </p>
          ) : (
            galleryImages.map((image, index) => (
              <div
                key={image.id || index}
                className="relative group rounded-lg overflow-hidden aspect-square bg-slate-700"
                data-testid={`gallery-image-${index}`}
              >
                <img
                  src={image.url}
                  alt={image.filename || `Galerie ${index + 1}`}
                  className="w-full h-full object-cover transition-transform group-hover:scale-110"
                  onError={(e) => {
                    console.error('Image load error:', image.url);
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<div class="flex items-center justify-center h-full text-red-400 text-xs p-2">Erreur chargement<br/>${image.filename}</div>`;
                  }}
                  onLoad={() => console.log('Image loaded:', image.filename)}
                />
                {isAdmin && (
                  <button
                    onClick={() => onImageDelete(image.id)}
                    className="absolute top-2 right-2 p-2 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid="delete-image-btn"
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MultimediaSection;